import type { ExportDestinationContent, ExportStructureNode } from '@capture/shared';
import { StructureNode, type StructureMapping } from './StructureNode';
import styles from './export-composer.module.css';

interface Props {
  structure: ExportStructureNode | undefined;
  onChange: (structure: ExportStructureNode) => void;
  /**
   * Mappings from the parent destination's Mapping tab. Field nodes added
   * via the picker pull `name` from `targetColumn` and `sourceField` from
   * the `sourceField` here — fixes #59 where the leaf rendered both sides
   * with the same string.
   */
  mappings: StructureMapping[];
  format: ExportDestinationContent['format'];
}

/** Moves item at `fromIdx` to `toIdx` in a copy of `children`. */
export function reorderChildren<T>(children: T[], fromIdx: number, toIdx: number): T[] {
  if (fromIdx === toIdx) return [...children];
  const result = [...children];
  const removed = result.splice(fromIdx, 1);
  result.splice(toIdx, 0, removed[0] as T);
  return result;
}

export function StructureBuilder({ structure, onChange, mappings, format }: Props) {
  // Flat formats don't support hierarchical structure
  if (format === 'csv' || format === 'fixed-width') {
    return (
      <div className={`${styles.structureBuilder} ${styles.structureBuilderFlat}`}>
        <p>
          Flat formats use field order from the Mapping tab. Structure grouping applies to XML and
          JSON only.
        </p>
      </div>
    );
  }

  // JSON/XML — no structure defined yet
  if (!structure) {
    // Seed from mappings when available so operators don't have to re-add
    // each mapped field as a leaf node by hand. (#55)
    const hasMappings = mappings.length > 0;
    return (
      <div className={`${styles.structureBuilder} ${styles.structureBuilderEmpty}`}>
        <p>No structure defined. Fields will export flat.</p>
        <button
          type="button"
          className={styles.structureCreateBtn}
          onClick={() =>
            onChange({
              name: 'root',
              type: 'group',
              children: hasMappings
                ? mappings.map<ExportStructureNode>((m) => ({
                    name: m.targetColumn,
                    type: 'field',
                    sourceField: m.sourceField,
                  }))
                : [],
            })
          }
        >
          {hasMappings
            ? `Create Structure from ${mappings.length} mapping${mappings.length === 1 ? '' : 's'}`
            : 'Create Structure'}
        </button>
      </div>
    );
  }

  // JSON/XML — render tree
  return (
    <div className={styles.structureBuilder}>
      <StructureNode
        node={structure}
        depth={0}
        onUpdate={onChange}
        onDelete={() => {
          // Deleting root resets to empty state
          onChange({ name: 'root', type: 'group', children: [] });
        }}
        mappings={mappings}
        rootStructure={structure}
        format={format as 'json' | 'xml'}
      />
    </div>
  );
}
